/**
 * Inpos M530 Fiscal Device Integration
 * Inpos M530 Yazarkasa Entegrasyonu
 */

import {
  IFiscalDevice,
  FiscalProductItem,
  FiscalOperationResult,
  FiscalApiResponse,
} from './types';
import { fetchLocalServiceWithFallback } from './localServiceFetch';

// Yazarkasa API endpoint'leri (varsayılan, setLocalServiceUrl ile değiştirilebilir)
const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';
let currentLocalServiceUrl = DEFAULT_LOCAL_SERVICE_URL;

// ✅ YENİ: Yerel servis URL'sini ayarla (QuickSaleScreen'den çağrılır)
export const setLocalServiceUrl = (url: string) => {
  if (url && url !== currentLocalServiceUrl) {
    console.log('[InPos] 🔌 Yerel servis URL güncellendi:', url);
    currentLocalServiceUrl = url;
  }
};

// Dinamik API base URL'i hesapla
const getLocalServiceApiBaseUrl = () => `${currentLocalServiceUrl}/api/yazarkasa`;

// ✅ Yedek adrese (localhost:3003) yalnızca güvenli okuma çağrıları tekrar gönderilir;
// ödeme/satış komutları TEKRAR GÖNDERİLMEZ (çift çekim önlemi). Bkz. localServiceFetch.ts
const fetchWithFallback = (url: string, options: RequestInit): Promise<Response> =>
  fetchLocalServiceWithFallback(url, options, currentLocalServiceUrl, DEFAULT_LOCAL_SERVICE_URL);

// Yazarkasa API çağrıları için yardımcı fonksiyon
const callFiscalPrinterAPI = async (
  endpoint: string,
  body: any,
  showAlertModal?: (title: string, message: string) => void
): Promise<FiscalApiResponse> => {
  try {
    const response = await fetchWithFallback(`${getLocalServiceApiBaseUrl()}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP error! status: ${response.status}`,
      }));
      console.error(`[Inpos M530] API error (${endpoint}):`, errorData);
      return { success: false, data: errorData, rawResponse: response };
    }
    
    const data = await response.json();
    
    if (data.success === false || data.errorCode) {
      console.error(`[Inpos M530] Operation error (${endpoint}):`, data);
      
      let detailedError = data.message || data.ErrorMessage || 'Bilinmeyen hata';
      
      // ⚠️ Kart red hatası ise MODAL AÇMA! (Frontend zaten kendi modalını açıyor)
      const isCardRejected = detailedError.includes('red edildi') || detailedError.includes('iptal edildi');
      
      return { success: false, data, rawResponse: response };
    }
    
    console.log(`[Inpos M530] API success (${endpoint}):`, data);
    return { success: true, data, rawResponse: response };
  } catch (error: any) {
    console.error(`[Inpos M530] API general error (${endpoint}):`, error);
    return { success: false, data: { message: error.message }, rawResponse: null };
  }
};

/**
 * Inpos M530 yazarkasa entegrasyonu
 */
export class InposM530Device implements IFiscalDevice {
  /**
   * Nakit ödeme için Inpos M530 yazarkasa işlemi
   */
  async processCashPayment(
    products: FiscalProductItem[],
    totalAmount: number,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    console.log('[Inpos M530] Nakit ödeme işlemi başlıyor...');
    
    try {
      // ADIM 1: Bağlantı kontrolü (isteğe bağlı - bağlantı zaten kurulmuş olmalı)
      // NOT: Local service açıldığında otomatik bağlanıyor, bu yüzden tekrar connect'e gerek yok
      
      // ADIM 2: Durum kontrolü - Kasiyer girişi yapılmış mı?
      console.log('[Inpos M530] 1. Cihaz durumu sorgulanıyor...');
      const stateResponse = await callFiscalPrinterAPI('inpos-get-state', {}, showAlertModal);
      
      if (!stateResponse.success) {
        throw new Error('Cihaz durumu sorgulanamadı. Bağlantıyı kontrol edin.');
      }
      
      // ✅ DÜZELTME: ecrStateCode ve saleStateCode kullan (sayı değerleri)
      const ecrState = stateResponse.data?.data?.ecrStateCode; // ✅ Sayı (int)
      const ecrStateName = stateResponse.data?.data?.ecrState; // String (debug için)
      const saleState = stateResponse.data?.data?.saleStateCode; // ✅ Sayı (int)
      const saleStateName = stateResponse.data?.data?.saleState; // String (debug için)
      
      console.log(`[Inpos M530] ECR State: ${ecrState} (${ecrStateName}) - type: ${typeof ecrState}`);
      console.log(`[Inpos M530] Sale State: ${saleState} (${saleStateName})`);
      
      // ECR State kontrolü:
      // 7 = InposEcrMainMenu (kasiyer girişi yapılmış, hazır)
      // 4 = InposEcrLogin (kasiyer girişi bekleniyor)
      // 5 = InposEcrSale (aktif satış var - iptal edilmeli)
      // 6 = InposEcrSaleWithInvoice (aktif faturalı satış var - iptal edilmeli)
      
      if (ecrState === 5 || ecrState === 6) {
        // Aktif satış varsa iptal et
        console.log('[Inpos M530] ⚠️ Aktif satış var, iptal ediliyor...');
        await callFiscalPrinterAPI('inpos-cancel-sale', {}, showAlertModal);
      }
      
      // ✅ ECR State 7 (Ana Menü) ise zaten giriş yapılmış - ATLA
      if (ecrState === 7) {
        console.log('[Inpos M530] ✅ Zaten ana menüde - Kasiyer girişi atlanıyor');
      } else if (ecrState === 4) {
        // ECR State 4 (Login ekranı) ise giriş yap
        console.log('[Inpos M530] ⚠️ Kasiyer giriş ekranında - Giriş yapılıyor...');
        const loginResponse = await callFiscalPrinterAPI('inpos-login', {
          cashierNo: '1',
          password: '1234'
        }, showAlertModal);
        
        if (!loginResponse.success) {
          throw new Error('Kasiyer girişi başarısız. Lütfen cihazın ana ekranda veya giriş ekranında olduğundan emin olun.');
        }
        
        console.log('[Inpos M530] ✅ Kasiyer girişi başarılı');
      } else {
        // Diğer durumlar (0,1,2,3,8,9,10,13) - Login denemesi yap
        console.log(`[Inpos M530] ⚠️ Beklenmeyen ECR State (${ecrState}) - Login denemesi yapılıyor...`);
        const loginResponse = await callFiscalPrinterAPI('inpos-login', {
          cashierNo: '1',
          password: '1234'
        }, showAlertModal);
        
        // Başarısız olsa bile devam et (bazı durumlarda cihaz otomatik ana menüye geçebilir)
        if (loginResponse.success) {
          console.log('[Inpos M530] ✅ Kasiyer girişi başarılı');
        } else {
          console.warn('[Inpos M530] ⚠️ Login başarısız ama devam ediliyor...');
        }
      }
      
      // ADIM 3: Satış Başlat
      console.log('[Inpos M530] 2. Satış başlatılıyor...');
      const startSaleResponse = await callFiscalPrinterAPI('inpos-start-sale', {}, showAlertModal);
      
      if (!startSaleResponse.success) {
        throw new Error('Satış başlatılamadı.');
      }
      
      console.log('[Inpos M530] ✅ Satış başarıyla başlatıldı');
      
      // ADIM 4: Ürünleri Ekle
      console.log(`[Inpos M530] 3. ${products.length} ürün ekleniyor...`);
      
      // ✅ ÖN HESAPLAMA: Yuvarlama farkını tespit et (indirimli sepetlerde oluşabilir)
      const targetTotalKurus = Math.round(totalAmount * 100);
      let calculatedTotalKurus = 0;
      
      console.log(`[Inpos M530] 📊 YUVARLAMA ANALİZİ - Hedef: ${totalAmount.toFixed(2)} TL (${targetTotalKurus} kuruş)`);
      
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        const priceWithVat = product.price * (1 + product.vatRate / 100);
        const unitPriceInKurus = Math.round(priceWithVat * 100);
        const itemTotalKurus = unitPriceInKurus * product.quantity;
        calculatedTotalKurus += itemTotalKurus;
        
        console.log(`[Inpos M530] 📊 ${product.name}: ${product.price.toFixed(4)} TL (KDV hariç) → ${priceWithVat.toFixed(4)} TL (KDV dahil) → ${unitPriceInKurus} kuruş x ${product.quantity} = ${itemTotalKurus} kuruş`);
      }
      
      const farkKurus = targetTotalKurus - calculatedTotalKurus;
      console.log(`[Inpos M530] 📊 Hesaplanan: ${calculatedTotalKurus} kuruş, Hedef: ${targetTotalKurus} kuruş, FARK: ${farkKurus} kuruş`);
      
      // ✅ FARK DÜZELTME: Son ürünün fiyatına farkı ekle (max 50 kuruş tolerans)
      let adjustedAmount = 0;
      
      if (farkKurus !== 0 && Math.abs(farkKurus) <= 50 && products.length > 0) {
        adjustedAmount = farkKurus;
        console.log(`[Inpos M530] 🔧 Yuvarlama düzeltmesi yapılacak: ${farkKurus} kuruş (${(farkKurus/100).toFixed(2)} TL)`);
      } else if (farkKurus !== 0) {
        console.warn(`[Inpos M530] ⚠️ Fark çok büyük (${farkKurus} kuruş), düzeltme yapılmadı!`);
      }
      
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        const productName = product.name.length > 24 ? product.name.substring(0, 24) : product.name;
        
        // ✅ ÖNEMLİ: Inpos fiyat ve miktar formatı:
        // - unitPrice: TL → Kuruş çevirme (100 ile çarp) - KDV DAHİL
        // - multiplier: Miktar * 1000 (Inpos formatı)
        // - unit: 0 (Adet)
        // - section: KDV grubu (1-8 arası)
        
        // ✅ KDV DAHİL fiyat hesapla (Hugin ile aynı)
        const priceWithVat = product.price * (1 + product.vatRate / 100);
        let unitPriceInKurus = Math.round(priceWithVat * 100);
        
        const isLastItemCash = i === products.length - 1;
        
        if (isLastItemCash && adjustedAmount !== 0) {
          if (product.quantity === 1) {
            console.log(`[Inpos M530] 🔧 ${productName} fiyatına ${adjustedAmount} kuruş ekleniyor...`);
            unitPriceInKurus += adjustedAmount;
            adjustedAmount = 0;
            console.log(`[Inpos M530] 🔧 Düzeltilmiş fiyat: ${unitPriceInKurus} kuruş`);
          } else {
            const normalQty = product.quantity - 1;
            const adjustedPrice = unitPriceInKurus + adjustedAmount;
            
            console.log(`[Inpos M530] 🔧 ${productName} bölünüyor: ${normalQty} x ${unitPriceInKurus} kuruş + 1 x ${adjustedPrice} kuruş`);
            
            const firstPartResp = await callFiscalPrinterAPI('inpos-add-item', {
              name: productName,
              unitPrice: unitPriceInKurus,
              multiplier: Math.round(normalQty * 1000),
              discountRate: 0,
              discountAmount: 0,
              section: product.departmentNo,
              unit: 0,
              timeout: 5000
            }, showAlertModal);
            
            if (!firstPartResp.success) {
              throw new Error(`Ürün eklenemedi: ${productName}`);
            }
            
            const secondPartResp = await callFiscalPrinterAPI('inpos-add-item', {
              name: productName,
              unitPrice: adjustedPrice,
              multiplier: 1000,
              discountRate: 0,
              discountAmount: 0,
              section: product.departmentNo,
              unit: 0,
              timeout: 5000
            }, showAlertModal);
            
            if (!secondPartResp.success) {
              throw new Error(`Ürün eklenemedi (düzeltilmiş): ${productName}`);
            }
            
            adjustedAmount = 0;
            console.log(`   ✅ Ürün ${i + 1}/${products.length} eklendi (bölünmüş): ${productName}`);
            continue;
          }
        }
        
        console.log(`   [${i + 1}/${products.length}] Ekleniyor: ${productName} x ${product.quantity}`);
        console.log(`   Fiyat (KDV Dahil): ${(unitPriceInKurus/100).toFixed(2)} TL (${unitPriceInKurus} Kuruş)`);
        
        const addItemResponse = await callFiscalPrinterAPI('inpos-add-item', {
          name: productName,
          unitPrice: unitPriceInKurus,
          multiplier: Math.round(product.quantity * 1000),
          discountRate: 0,
          discountAmount: 0,
          section: product.departmentNo,
          unit: 0,
          timeout: 5000
        }, showAlertModal);
        
        if (!addItemResponse.success) {
          throw new Error(`Ürün eklenemedi: ${productName}`);
        }
        
        console.log(`   ✅ Ürün ${i + 1}/${products.length} eklendi: ${productName}`);
      }
      
      console.log('[Inpos M530] ✅ Tüm ürünler başarıyla eklendi');
      
      // ADIM 5: NAKİT ÖDEMESİ EKLE - FIRE AND FORGET! 🚀
      console.log('[Inpos M530] 4. 💵 Nakit ödeme gönderiliyor (beklenmeden)...');
      
      const cashAmountInKurus = Math.round(totalAmount * 100);
      
      // ⚠️ NAKİT ÖDEMEDE SONUCU BEKLEMİYORUZ! (Fire-and-forget)
      // Inpos M530 otomatik olarak belgeyi kapatıp fiş yazdırıyor
      // Biz sadece isteği gönderiyoruz ve HEMEN dönüyoruz!
      callFiscalPrinterAPI('inpos-add-payment', {
        paymentType: 1, // ✅ 1 = Nakit (InposExt SDK)
        amount: cashAmountInKurus,
        acquirer: 0, // Varsayılan acquirer
        timeout: 10000
      }, showAlertModal).then(response => {
        if (response.success) {
          console.log('[Inpos M530] ✅ Nakit ödeme arka planda başarıyla tamamlandı');
        } else {
          console.error('[Inpos M530] ❌ Nakit ödeme arka planda hata aldı:', response.data?.message);
        }
      }).catch(error => {
        console.error('[Inpos M530] ❌ Nakit ödeme arka planda exception:', error);
      });
      
      console.log('[Inpos M530] ✅ Nakit ödeme gönderildi! (Arka planda işleniyor - BİZ DEVAM EDİYORUZ!)');
      
      return {
        success: true,
        documentId: 'ASYNC-SENT', // Async gönderildi, beklenmedi
      };
      
    } catch (error: any) {
      console.error('[Inpos M530] Nakit ödeme hatası:', error);
      
      // ⚠️ Kart red hatası ise CANCEL YAPMA! (Cihaz zaten kart okuma döngüsünde)
      const isCardRejected = error.message?.includes('red edildi') || error.message?.includes('iptal edildi');
      
      if (isCardRejected) {
        console.log('[Inpos M530] ⚠️ Kart red edildi - Cancel atlanıyor (cihaz zaten hazır)');
      } else {
        // Diğer hatalar için satışı iptal et
        try {
          console.log('[Inpos M530] ❌ Hata nedeniyle satış iptal ediliyor...');
          await callFiscalPrinterAPI('inpos-cancel-sale', {}, showAlertModal);
          console.log('[Inpos M530] ✅ Satış iptal edildi');
        } catch (cancelError) {
          console.error('[Inpos M530] Satış iptal edilirken hata:', cancelError);
        }
      }
      
      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * POS ödeme için Inpos M530 yazarkasa işlemi
   */
  async processPosPayment(
    products: FiscalProductItem[],
    totalAmount: number,
    showAlertModal?: (title: string, message: string) => void,
    cashAmount?: number, // ✅ Karma ödeme için nakit tutarı
    companyId?: string // ✅ PavoPay için satış kaydı (inpos'da kullanılmaz)
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Inpos M530] POS ödeme işlemi başlıyor...');
      console.log(`[Inpos M530] Toplam tutar: ${totalAmount} TL`);
      if (cashAmount) {
        console.log(`[Inpos M530] 💵 Nakit: ${cashAmount} TL, 💳 Kredi Kartı: ${totalAmount - cashAmount} TL`);
      }
      
      // 1️⃣ DURUM KONTROLÜ
      console.log('[Inpos M530] 1. Cihaz durumu sorgulanıyor...');
      const stateResponse = await callFiscalPrinterAPI('inpos-get-state', {}, showAlertModal);
      
      if (!stateResponse.success) {
        throw new Error('Cihaz durumu sorgulanamadı');
      }

      const ecrState = stateResponse.data?.data?.ecrStateCode; // ✅ Sayı (int)
      const ecrStateName = stateResponse.data?.data?.ecrState; // String (debug için)
      const saleState = stateResponse.data?.data?.saleStateCode; // ✅ Sayı (int)
      const saleStateName = stateResponse.data?.data?.saleState; // String (debug için)

      console.log(`[Inpos M530] ECR State: ${ecrState} (${ecrStateName}) - type: ${typeof ecrState}`);
      console.log(`[Inpos M530] Sale State: ${saleState} (${saleStateName})`);

      // Eğer aktif satış varsa iptal et
      if (ecrState === 5 || ecrState === 6) { // InposEcrSale veya InposEcrSaleWithInvoice
        console.log('[Inpos M530] ⚠️ Aktif satış var, iptal ediliyor...');
        await callFiscalPrinterAPI('inpos-cancel-sale', {}, showAlertModal);
      }

      // Eğer ana menüde değilse kasiyer girişi yap
      if (ecrState === 7) {
        console.log('[Inpos M530] ✅ Zaten ana menüde - Kasiyer girişi atlanıyor');
      } else if (ecrState === 4) {
        console.log('[Inpos M530] ⚠️ Kasiyer giriş ekranında - Giriş yapılıyor...');
        const loginResponse = await callFiscalPrinterAPI('inpos-login', {
          timeout: 30000 // 30 saniye
        }, showAlertModal);
        
        if (!loginResponse.success) {
          throw new Error('Kasiyer girişi başarısız');
        }
        console.log('[Inpos M530] ✅ Kasiyer girişi başarılı');
      } else {
        console.log(`[Inpos M530] ⚠️ Beklenmeyen ECR State (${ecrState}) - Login denemesi yapılıyor...`);
        const loginResponse = await callFiscalPrinterAPI('inpos-login', {
          timeout: 30000
        }, showAlertModal);
        
        if (!loginResponse.success) {
          throw new Error('Kasiyer girişi başarısız');
        }
        console.log('[Inpos M530] ✅ Kasiyer girişi başarılı');
      }
      
      // 2️⃣ SATIŞ BAŞLAT
      console.log('[Inpos M530] 2. Satış başlatılıyor...');
      const startSaleResponse = await callFiscalPrinterAPI('inpos-start-sale', {}, showAlertModal);
      
      if (!startSaleResponse.success) {
        throw new Error('Satış başlatılamadı');
      }
      
      console.log('[Inpos M530] ✅ Satış başarıyla başlatıldı');
      
      // 3️⃣ ÜRÜNLERİ EKLE
      console.log(`[Inpos M530] 3. ${products.length} ürün ekleniyor...`);
      
      // ✅ ÖN HESAPLAMA: Yuvarlama farkını tespit et
      const targetTotalKurus = Math.round(totalAmount * 100);
      let calculatedTotalKurus = 0;
      const productKurusValues: number[] = [];
      
      console.log(`[Inpos M530] 📊 YUVARLAMA ANALİZİ - Hedef: ${totalAmount.toFixed(2)} TL (${targetTotalKurus} kuruş)`);
      
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        const vatRate = product.vatRate || 20;
        const priceWithVat = product.price * (1 + vatRate / 100);
        const unitPriceInKurus = Math.round(priceWithVat * 100);
        const itemTotalKurus = unitPriceInKurus * product.quantity;
        
        productKurusValues.push(unitPriceInKurus);
        calculatedTotalKurus += itemTotalKurus;
        
        console.log(`[Inpos M530] 📊 ${product.name}: ${product.price.toFixed(4)} TL (KDV hariç) → ${priceWithVat.toFixed(4)} TL (KDV dahil) → ${unitPriceInKurus} kuruş x ${product.quantity} = ${itemTotalKurus} kuruş`);
      }
      
      const farkKurus = targetTotalKurus - calculatedTotalKurus;
      console.log(`[Inpos M530] 📊 Hesaplanan: ${calculatedTotalKurus} kuruş, Hedef: ${targetTotalKurus} kuruş, FARK: ${farkKurus} kuruş`);
      
      // ✅ FARK DÜZELTME: Son ürünün fiyatına farkı ekle (max 50 kuruş tolerans)
      let adjustedProductIndex = -1;
      let adjustedAmount = 0;
      
      if (farkKurus !== 0 && Math.abs(farkKurus) <= 50 && products.length > 0) {
        // Son ürünün fiyatına farkı ekle (quantity > 1 ise son birime)
        adjustedProductIndex = products.length - 1;
        const lastProduct = products[adjustedProductIndex];
        
        // Fark son ürünün her birimine mi yoksa sadece toplama mı eklenmeli?
        // Inpos SDK'da unitPrice x multiplier ile toplam hesaplandığı için,
        // quantity > 1 ise farkı quantity'ye bölerek birim fiyatına ekleyemeyiz
        // Bu yüzden tek seferlik "yuvarlama düzeltme" ürünü ekleyeceğiz
        adjustedAmount = farkKurus;
        console.log(`[Inpos M530] 🔧 Yuvarlama düzeltmesi yapılacak: ${farkKurus} kuruş (${(farkKurus/100).toFixed(2)} TL)`);
      } else if (farkKurus !== 0) {
        console.warn(`[Inpos M530] ⚠️ Fark çok büyük (${farkKurus} kuruş), düzeltme yapılmadı!`);
      }
      
      // Ürünleri Inpos'a gönder
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        
        // KDV dahil fiyat hesapla
        const vatRate = product.vatRate || 20; // Varsayılan %20
        const priceWithVat = product.price * (1 + vatRate / 100);
        let unitPriceInKurus = Math.round(priceWithVat * 100); // Kuruş cinsine çevir
        
        const isLastItemPos = i === adjustedProductIndex;
        
        if (isLastItemPos && adjustedAmount !== 0) {
          if (product.quantity === 1) {
            console.log(`[Inpos M530] 🔧 ${product.name} fiyatına ${adjustedAmount} kuruş ekleniyor...`);
            unitPriceInKurus += adjustedAmount;
            adjustedAmount = 0;
            console.log(`[Inpos M530] 🔧 Düzeltilmiş fiyat: ${unitPriceInKurus} kuruş`);
          } else {
            const normalQty = product.quantity - 1;
            const adjustedPrice = unitPriceInKurus + adjustedAmount;
            
            console.log(`[Inpos M530] 🔧 ${product.name} bölünüyor: ${normalQty} x ${unitPriceInKurus} kuruş + 1 x ${adjustedPrice} kuruş`);
            
            const firstPartResp = await callFiscalPrinterAPI('inpos-add-item', {
              name: product.name,
              unitPrice: unitPriceInKurus,
              multiplier: Math.round(normalQty * 1000),
              discountRate: 0,
              discountAmount: 0,
              section: product.departmentNo || 1,
              unit: 0,
              timeout: 5000
            }, showAlertModal);
            
            if (!firstPartResp.success) {
              throw new Error(`Ürün eklenemedi: ${product.name}`);
            }
            
            const secondPartResp = await callFiscalPrinterAPI('inpos-add-item', {
              name: product.name,
              unitPrice: adjustedPrice,
              multiplier: 1000,
              discountRate: 0,
              discountAmount: 0,
              section: product.departmentNo || 1,
              unit: 0,
              timeout: 5000
            }, showAlertModal);
            
            if (!secondPartResp.success) {
              throw new Error(`Ürün eklenemedi (düzeltilmiş): ${product.name}`);
            }
            
            adjustedAmount = 0;
            console.log(`   ✅ Ürün ${i+1}/${products.length} eklendi (bölünmüş): ${product.name}`);
            continue;
          }
        }
        
        console.log(`   [${i+1}/${products.length}] Ekleniyor: ${product.name} x ${product.quantity}`);
        console.log(`   Fiyat (KDV Dahil): ${(unitPriceInKurus/100).toFixed(2)} TL (${unitPriceInKurus} Kuruş)`);
        
        const addItemResponse = await callFiscalPrinterAPI('inpos-add-item', {
          name: product.name,
          unitPrice: unitPriceInKurus,
          multiplier: Math.round(product.quantity * 1000),
          discountRate: 0,
          discountAmount: 0,
          section: product.departmentNo || 1,
          unit: 0,
          timeout: 5000
        }, showAlertModal);
        
        if (!addItemResponse.success) {
          throw new Error(`Ürün eklenemedi: ${product.name}`);
        }
        
        console.log(`   ✅ Ürün ${i+1}/${products.length} eklendi: ${product.name}`);
      }
      
      console.log('[Inpos M530] ✅ Tüm ürünler başarıyla eklendi');
      
      // 4️⃣ POS ÖDEMESİ EKLE (DÖKÜMAN YÖNTEMİ!)
      console.log('[Inpos M530] 4. 💳 POS ödeme ekleniyor...');
      console.log('[Inpos M530] ⚠️ Cihazda kart okutulması bekleniyor...');

      const posAmountInKurus = Math.round(totalAmount * 100);

      // ✅ DÖKÜMAN: AddPayment() başarı/başarısızlık dönüyor!
      const addPaymentResponse = await callFiscalPrinterAPI('inpos-add-payment', {
        paymentType: 0, // ✅ 0 = Kredi Kartı (InposExt SDK)
        amount: posAmountInKurus,
        acquirer: 0, // Varsayılan acquirer
        timeout: 60000 // 60 saniye - Kart okuma için uzun timeout
      }, showAlertModal);
      
      if (!addPaymentResponse.success) {
        const posApiErrorMsg = addPaymentResponse.data?.message || 'POS ödeme başarısız';
        const isPosCardRejected = posApiErrorMsg.includes('red edildi') || posApiErrorMsg.includes('iptal edildi');
        
        if (isPosCardRejected) {
          console.warn(`[Inpos M530] ❌ POS kart red/iptal edildi: ${posApiErrorMsg}`);
          throw new Error(posApiErrorMsg);
        }
        
        console.warn('[Inpos M530] ⚠️ POS ödeme API yanıtı başarısız! Gerçek durum kontrol ediliyor...');
        console.warn('[Inpos M530] API Yanıtı:', posApiErrorMsg);
        
        const verifyStateResponse = await callFiscalPrinterAPI('inpos-get-state', {});
        
        if (verifyStateResponse.success) {
          const verifyEcrState = verifyStateResponse.data?.data?.ecrStateCode;
          const verifySaleState = verifyStateResponse.data?.data?.saleStateCode;
          
          console.log(`[Inpos M530] 🔍 Doğrulama - ECR State: ${verifyEcrState}, Sale State: ${verifySaleState}`);
          
          // Sadece fiş yazdırma veya satış tamamlanma durumlarında başarılı say
          // ECR=7 (MainMenu) ve Sale=1 (WaitingForInput) dahil DEĞİL - red sonrası da bu durumlarda kalabiliyor
          if (verifyEcrState === 10 || verifyEcrState === 11 || 
              verifySaleState === 0 || verifySaleState === 15) {
            console.log('[Inpos M530] ✅ ÖDEME ASLINDA BAŞARILI! Cihaz fiş yazdırdı/tamamlandı.');
            
            if (verifyEcrState === 10 || verifyEcrState === 11 || verifySaleState === 15) {
              console.log('[Inpos M530] ⏳ Fiş yazdırılıyor, 2 saniye bekleniyor...');
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
            
            return {
              success: true,
              documentId: 'PAYMENT-VERIFIED-OK',
            };
          }
        }
        
        throw new Error(posApiErrorMsg);
      }
      
      console.log('[Inpos M530] ✅ POS ödeme başarıyla eklendi');

      // 5️⃣ DURUM KONTROLÜ: Inpos M530 tam tutar ödeme aldığında otomatik kapatıyor!
      console.log('[Inpos M530] 5. 📄 Belge durumu kontrol ediliyor...');
      const checkStateResponse = await callFiscalPrinterAPI('inpos-get-state', {}, showAlertModal);
      
      if (checkStateResponse.success) {
        const ecrState = checkStateResponse.data?.data?.ecrStateCode;
        const saleState = checkStateResponse.data?.data?.saleStateCode;
        
        console.log(`[Inpos M530] Durum: ECR=${ecrState}, Sale=${saleState}`);
        
        // ECR=7 (MainMenu), ECR=10/11 (Printing), Sale=0 (Idle), Sale=15 (DataFinalized)
        if (ecrState === 7 || ecrState === 10 || ecrState === 11 || saleState === 0 || saleState === 15) {
          console.log('[Inpos M530] ✅ Inpos belgeyi otomatik kapattı! (Fiş yazdırılıyor/yazdırıldı)');
          
          // Fiş yazdırma için kısa bir süre bekle
          if (ecrState === 10 || ecrState === 11 || saleState === 15) {
            console.log('[Inpos M530] ⏳ Fiş yazdırılıyor, 2 saniye bekleniyor...');
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
          
          return {
            success: true,
            documentId: 'AUTO-FINALIZED-POS',
          };
        }
      }
      
      // Eğer hala açıksa manuel kapat
      console.log('[Inpos M530] ⚠️ Belge hala açık, manuel kapatılıyor...');
      const finalizeResponse = await callFiscalPrinterAPI('inpos-finalize-sale', {
        timeout: 10000
      }, showAlertModal);

      if (!finalizeResponse.success) {
        throw new Error('Belge kapatılamadı');
      }

      console.log('[Inpos M530] ✅ Belge başarıyla kapatıldı');
      
      // Fiş bilgileri
      const receiptNo = finalizeResponse.data?.data?.receiptNo || 'Bilinmiyor';
      const zNo = finalizeResponse.data?.data?.zNo || 'Bilinmiyor';
      
      console.log(`[Inpos M530] 📄 Fiş No: ${receiptNo}, Z No: ${zNo}`);
      
      return {
        success: true,
        documentId: `${receiptNo}-${zNo}`,
      };
      
    } catch (error: any) {
      console.error('[Inpos M530] POS ödeme hatası:', error);
      
      const isCardRejected = error.message?.includes('red edildi') || error.message?.includes('iptal edildi');
      
      // Kart red/iptal ise doğrulama yapmadan hata döndür
      if (!isCardRejected) {
        try {
          console.log('[Inpos M530] 🔍 Hata sonrası cihaz durumu kontrol ediliyor...');
          const errorVerifyState = await callFiscalPrinterAPI('inpos-get-state', {});
          
          if (errorVerifyState.success) {
            const errorEcrState = errorVerifyState.data?.data?.ecrStateCode;
            const errorSaleState = errorVerifyState.data?.data?.saleStateCode;
            
            console.log(`[Inpos M530] 🔍 Hata sonrası durum - ECR: ${errorEcrState}, Sale: ${errorSaleState}`);
            
            // Sadece fiş yazdırma veya satış tamamlanma durumlarında başarılı say
            if (errorEcrState === 10 || errorEcrState === 11 || 
                errorSaleState === 0 || errorSaleState === 15) {
              console.log('[Inpos M530] ✅ HATA OLMASINA RAĞMEN ÖDEME BAŞARILI! Cihaz fiş yazdırdı/tamamlandı.');
              
              if (errorEcrState === 10 || errorEcrState === 11 || errorSaleState === 15) {
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
              
              return {
                success: true,
                documentId: 'ERROR-BUT-PAYMENT-OK',
              };
            }
          }
        } catch (verifyError) {
          console.error('[Inpos M530] Hata sonrası durum kontrolü başarısız:', verifyError);
        }
      }
      
      if (isCardRejected) {
        console.log('[Inpos M530] ❌ Kart red edildi veya iptal edildi - Yazarkasadaki sepet temizleniyor...');
        try {
          await callFiscalPrinterAPI('inpos-cancel-sale', {}, showAlertModal);
          console.log('[Inpos M530] ✅ Yazarkasadaki sepet temizlendi');
        } catch (cancelError) {
          console.error('[Inpos M530] ❌ Sepet temizlenirken hata:', cancelError);
        }
      } else {
        // Diğer hatalar için de satışı iptal et
        try {
          console.log('[Inpos M530] ❌ Hata nedeniyle satış iptal ediliyor...');
          await callFiscalPrinterAPI('inpos-cancel-sale', {}, showAlertModal);
          console.log('[Inpos M530] ✅ Satış iptal edildi');
        } catch (cancelError) {
          console.error('[Inpos M530] Satış iptal edilirken hata:', cancelError);
        }
      }
      
      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * Yazarkasa bağlantı kontrolü
   */
  async checkConnection(): Promise<boolean> {
    try {
      console.log('[Inpos M530] Bağlantı kontrolü yapılıyor...');
      const stateResponse = await callFiscalPrinterAPI('inpos-get-state', {});
      
      if (stateResponse.success) {
        console.log('[Inpos M530] ✅ Bağlantı başarılı');
        return true;
      }
      
      console.log('[Inpos M530] ❌ Bağlantı başarısız');
      return false;
    } catch (error) {
      console.error('[Inpos M530] Bağlantı kontrolü hatası:', error);
      return false;
    }
  }

  /**
   * Yazarkasa bilgilerini al
   */
  async getDeviceInfo(): Promise<any> {
    try {
      console.log('[Inpos M530] Cihaz bilgileri alınıyor...');
      const stateResponse = await callFiscalPrinterAPI('inpos-get-state', {});
      
      if (stateResponse.success && stateResponse.data?.data) {
        const deviceData = stateResponse.data.data;
        
        return {
          model: 'Inpos M530',
          status: 'Connected',
          ecrState: deviceData.ecrState,
          ecrStateName: deviceData.ecrStateName,
          saleState: deviceData.saleState,
          saleStateName: deviceData.saleStateName,
          version: stateResponse.data.version || 'Unknown',
        };
      }
      
      return {
        model: 'Inpos M530',
        status: 'Disconnected',
        message: 'Cihaz bilgileri alınamadı',
      };
    } catch (error: any) {
      console.error('[Inpos M530] Cihaz bilgileri alınırken hata:', error);
      return {
        model: 'Inpos M530',
        status: 'Error',
        message: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * Çoklu ödeme işlemi (Birden fazla POS + NAKİT)
   * Bu metod "Ödemeyi Tamamla (Hızlı)" butonu için kullanılır
   */
  async processMultiplePayments(
    products: FiscalProductItem[],
    posPayments: { id: string; amount: number }[],
    cashAmount?: number,
    showAlertModal?: (title: string, message: string) => void,
    stepCallback?: any
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Inpos M530] 🚀 Çoklu ödeme işlemi başlıyor...');
      console.log(`[Inpos M530]   POS Ödemeleri: ${posPayments.length} adet`);
      console.log(`[Inpos M530]   Nakit: ${cashAmount || 0} TL`);
      
      // ✅ ADIM 1: Belge başlat ve ürünleri ekle
      console.log('[Inpos M530] 1️⃣ Belge başlatılıyor ve ürünler ekleniyor...');
      const startResult = await this.startDocumentWithItems(products, showAlertModal);
      
      if (!startResult.success) {
        throw new Error(`Belge başlatılamadı: ${startResult.errorMessage}`);
      }
      
      console.log('[Inpos M530] ✅ Belge hazırlandı');
      
      // ✅ ADIM 2: POS ödemelerini ekle
      let posIndex = 0;
      for (const posPayment of posPayments) {
        console.log(`[Inpos M530] 2️⃣ POS Ödeme ${posIndex + 1}/${posPayments.length} ekleniyor...`);
        
        // Step callback - başlangıç
        stepCallback?.onStepStart?.(posIndex, 'pos', posPayment.amount, `POS Ödeme ${posIndex + 1}`);
        
        const posResult = await this.addSinglePayment('pos', posPayment.amount, showAlertModal);
        
        if (!posResult.success) {
          // Step callback - hata
          stepCallback?.onStepError?.(posIndex, 'pos', posPayment.amount, posResult.errorMessage || 'Bilinmeyen hata');
          throw new Error(`POS ödeme ${posIndex + 1} eklenemedi: ${posResult.errorMessage}`);
        }
        
        // Step callback - başarı
        stepCallback?.onStepSuccess?.(posIndex, 'pos', posPayment.amount, posResult.fiscalData);
        console.log(`[Inpos M530] ✅ POS Ödeme ${posIndex + 1} eklendi`);
        
        posIndex++;
      }
      
      // ✅ ADIM 3: Nakit ödeme varsa ekle
      if (cashAmount && cashAmount > 0) {
        console.log(`[Inpos M530] 3️⃣ Nakit ödeme ekleniyor: ${cashAmount} TL`);
        
        // Step callback - başlangıç
        stepCallback?.onStepStart?.(posPayments.length, 'cash', cashAmount, 'Nakit Ödeme');
        
        const cashResult = await this.addSinglePayment('cash', cashAmount, showAlertModal);
        
        if (!cashResult.success) {
          // Step callback - hata
          stepCallback?.onStepError?.(posPayments.length, 'cash', cashAmount, cashResult.errorMessage || 'Bilinmeyen hata');
          throw new Error(`Nakit ödeme eklenemedi: ${cashResult.errorMessage}`);
        }
        
        // Step callback - başarı
        stepCallback?.onStepSuccess?.(posPayments.length, 'cash', cashAmount, cashResult.fiscalData);
        console.log('[Inpos M530] ✅ Nakit ödeme eklendi');
      }
      
      // ✅ ADIM 4: Belgeyi kapat
      console.log('[Inpos M530] 4️⃣ Belge kapatılıyor...');
      const closeResult = await this.closeDocument(showAlertModal);
      
      if (!closeResult.success) {
        throw new Error(`Belge kapatılamadı: ${closeResult.errorMessage}`);
      }
      
      console.log('[Inpos M530] ✅ Çoklu ödeme işlemi tamamlandı');
      
      return {
        success: true,
        documentId: closeResult.documentId,
        fiscalData: closeResult.fiscalData,
      };
      
    } catch (error: any) {
      console.error('[Inpos M530] Çoklu ödeme hatası:', error);
      
      // Hata durumunda belgeyi iptal et
      try {
        console.log('[Inpos M530] ❌ Hata nedeniyle belge iptal ediliyor...');
        await this.cancelDocument(showAlertModal);
        console.log('[Inpos M530] ✅ Belge iptal edildi');
      } catch (cancelError) {
        console.error('[Inpos M530] Belge iptal edilirken hata:', cancelError);
      }
      
      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * "Canlı Belge" Modu - Belge başlat ve ürünleri ekle
   */
  async startDocumentWithItems(
    products: FiscalProductItem[],
    showAlertModal?: (title: string, message: string) => void,
    totalAmount?: number
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Inpos M530] 🟢 CANLI BELGE MODU: Belge hazırlanıyor...');
      
      // 1️⃣ DURUM KONTROLÜ
      console.log('[Inpos M530] 1. Cihaz durumu sorgulanıyor...');
      const stateResponse = await callFiscalPrinterAPI('inpos-get-state', {}, showAlertModal);
      
      if (!stateResponse.success) {
        throw new Error('Cihaz durumu sorgulanamadı');
      }

      const ecrState = stateResponse.data?.data?.ecrStateCode; // ✅ Sayı (int)
      const ecrStateName = stateResponse.data?.data?.ecrState; // String (debug için)
      
      console.log(`[Inpos M530] ECR State: ${ecrState} (${ecrStateName})`);
      
      // 2️⃣ KASİYER GİRİŞİ KONTROLÜ
      // 7 = InposEcrMainMenu (kasiyer girişi yapılmış, hazır)
      // 4 = InposEcrLogin (kasiyer girişi bekleniyor)
      // 5 = InposEcrSale (aktif satış var - iptal edilmeli)
      
      if (ecrState === 5 || ecrState === 6) {
        // Aktif satış varsa iptal et
        console.log('[Inpos M530] ⚠️ Aktif satış var, iptal ediliyor...');
        await callFiscalPrinterAPI('inpos-cancel-sale', {}, showAlertModal);
      }
      
      if (ecrState === 7) {
        console.log('[Inpos M530] ✅ Zaten ana menüde - Kasiyer girişi atlanıyor');
      } else if (ecrState === 4) {
        console.log('[Inpos M530] ⚠️ Kasiyer giriş ekranında - Giriş yapılıyor...');
        
        // ⏳ Cihazın stabil olmasını bekle (kullanıcı ekranda geziniyor olabilir)
        console.log('[Inpos M530] ⏳ Cihazın stabil olması bekleniyor (2 saniye)...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // 🔄 Retry mekanizması ekle (maksimum 3 deneme)
        let loginSuccess = false;
        let lastError = '';
        
        for (let attempt = 1; attempt <= 3; attempt++) {
          console.log(`[Inpos M530] 🔄 Kasiyer girişi denemesi ${attempt}/3...`);
          
          const loginResponse = await callFiscalPrinterAPI('inpos-login', {
            cashierNo: '1',
            password: '1234'
          });
          
          if (loginResponse.success) {
            loginSuccess = true;
            console.log('[Inpos M530] ✅ Kasiyer girişi başarılı');
            break;
          } else {
            lastError = loginResponse.data?.message || 'Login hatası';
            console.warn(`[Inpos M530] ⚠️ Deneme ${attempt}/3 başarısız: ${lastError}`);
            
            // Bir sonraki denemeden önce 1 saniye bekle
            if (attempt < 3) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
        
        if (!loginSuccess) {
          throw new Error(`Kasiyer girişi başarısız (3 deneme): ${lastError}`);
        }
      } else {
        console.log(`[Inpos M530] ⚠️ Beklenmeyen ECR State (${ecrState}) - Login denemesi yapılıyor...`);
        
        // ⏳ Cihazın stabil olmasını bekle
        console.log('[Inpos M530] ⏳ Cihazın stabil olması bekleniyor (2 saniye)...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // 🔄 Retry mekanizması
        let loginSuccess = false;
        let lastError = '';
        
        for (let attempt = 1; attempt <= 3; attempt++) {
          console.log(`[Inpos M530] 🔄 Kasiyer girişi denemesi ${attempt}/3...`);
          
          const loginResponse = await callFiscalPrinterAPI('inpos-login', {
            cashierNo: '1',
            password: '1234'
          });
          
          if (loginResponse.success) {
            loginSuccess = true;
            console.log('[Inpos M530] ✅ Kasiyer girişi başarılı');
            break;
          } else {
            lastError = loginResponse.data?.message || 'Login hatası';
            console.warn(`[Inpos M530] ⚠️ Deneme ${attempt}/3 başarısız: ${lastError}`);
            
            if (attempt < 3) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
        
        if (!loginSuccess) {
          throw new Error(`Kasiyer girişi başarısız (3 deneme): ${lastError}`);
        }
      }
      
      // 3️⃣ SATIŞ BAŞLAT
      console.log('[Inpos M530] 2. Satış başlatılıyor...');
      const startSaleResponse = await callFiscalPrinterAPI('inpos-start-sale', {}, showAlertModal);
      
      if (!startSaleResponse.success) {
        throw new Error('Satış başlatılamadı');
      }
      
      console.log('[Inpos M530] ✅ Satış başarıyla başlatıldı');
      
      // 4️⃣ ÜRÜNLERİ EKLE
      console.log(`[Inpos M530] 3. ${products.length} ürün ekleniyor...`);
      
      // ✅ ÖN HESAPLAMA: Yuvarlama farkını tespit et (indirimli sepetlerde oluşabilir)
      let adjustedAmount = 0;
      
      if (totalAmount != null) {
        const targetTotalKurus = Math.round(totalAmount * 100);
        let calculatedTotalKurus = 0;
        
        console.log(`[Inpos M530] 📊 YUVARLAMA ANALİZİ - Hedef: ${totalAmount.toFixed(2)} TL (${targetTotalKurus} kuruş)`);
        
        for (let i = 0; i < products.length; i++) {
          const product = products[i];
          const priceWithVat = product.price * (1 + product.vatRate / 100);
          const unitPriceInKurus = Math.round(priceWithVat * 100);
          const itemTotalKurus = unitPriceInKurus * product.quantity;
          calculatedTotalKurus += itemTotalKurus;
          
          console.log(`[Inpos M530] 📊 ${product.name}: ${product.price.toFixed(4)} TL (KDV hariç) → ${priceWithVat.toFixed(4)} TL (KDV dahil) → ${unitPriceInKurus} kuruş x ${product.quantity} = ${itemTotalKurus} kuruş`);
        }
        
        const farkKurus = targetTotalKurus - calculatedTotalKurus;
        console.log(`[Inpos M530] 📊 Hesaplanan: ${calculatedTotalKurus} kuruş, Hedef: ${targetTotalKurus} kuruş, FARK: ${farkKurus} kuruş`);
        
        if (farkKurus !== 0 && Math.abs(farkKurus) <= 50 && products.length > 0) {
          adjustedAmount = farkKurus;
          console.log(`[Inpos M530] 🔧 Yuvarlama düzeltmesi yapılacak: ${farkKurus} kuruş (${(farkKurus/100).toFixed(2)} TL)`);
        } else if (farkKurus !== 0) {
          console.warn(`[Inpos M530] ⚠️ Fark çok büyük (${farkKurus} kuruş), düzeltme yapılmadı!`);
        }
      }
      
      for (let i = 0; i < products.length; i++) {
        const product = products[i];
        const productName = product.name.length > 24 ? product.name.substring(0, 24) : product.name;
        
        const priceWithVat = product.price * (1 + product.vatRate / 100);
        let unitPriceInKurus = Math.round(priceWithVat * 100);
        
        const isLastItem = i === products.length - 1;
        
        if (isLastItem && adjustedAmount !== 0) {
          if (product.quantity === 1) {
            console.log(`[Inpos M530] 🔧 ${productName} fiyatına ${adjustedAmount} kuruş ekleniyor...`);
            unitPriceInKurus += adjustedAmount;
            adjustedAmount = 0;
            console.log(`[Inpos M530] 🔧 Düzeltilmiş fiyat: ${unitPriceInKurus} kuruş`);
          } else {
            // quantity > 1: (quantity-1) adet normal fiyattan + 1 adet düzeltilmiş fiyattan
            const normalQty = product.quantity - 1;
            const adjustedPrice = unitPriceInKurus + adjustedAmount;
            
            console.log(`[Inpos M530] 🔧 ${productName} bölünüyor: ${normalQty} x ${unitPriceInKurus} kuruş + 1 x ${adjustedPrice} kuruş`);
            
            // İlk kısım: (quantity-1) adet normal fiyattan
            console.log(`   [${i + 1}a/${products.length}] Ekleniyor: ${productName} x ${normalQty}`);
            console.log(`   Fiyat (KDV Dahil): ${(unitPriceInKurus/100).toFixed(2)} TL (${unitPriceInKurus} Kuruş)`);
            
            const firstPartResponse = await callFiscalPrinterAPI('inpos-add-item', {
              name: productName,
              unitPrice: unitPriceInKurus,
              multiplier: Math.round(normalQty * 1000),
              discountRate: 0,
              discountAmount: 0,
              section: product.departmentNo,
              unit: 0,
              timeout: 5000
            }, showAlertModal);
            
            if (!firstPartResponse.success) {
              throw new Error(`Ürün eklenemedi: ${productName}`);
            }
            
            // İkinci kısım: 1 adet düzeltilmiş fiyattan
            console.log(`   [${i + 1}b/${products.length}] Ekleniyor: ${productName} x 1 (düzeltilmiş)`);
            console.log(`   Fiyat (KDV Dahil): ${(adjustedPrice/100).toFixed(2)} TL (${adjustedPrice} Kuruş)`);
            
            const secondPartResponse = await callFiscalPrinterAPI('inpos-add-item', {
              name: productName,
              unitPrice: adjustedPrice,
              multiplier: 1000,
              discountRate: 0,
              discountAmount: 0,
              section: product.departmentNo,
              unit: 0,
              timeout: 5000
            }, showAlertModal);
            
            if (!secondPartResponse.success) {
              throw new Error(`Ürün eklenemedi (düzeltilmiş): ${productName}`);
            }
            
            adjustedAmount = 0;
            console.log(`   ✅ Ürün ${i + 1}/${products.length} eklendi (bölünmüş): ${productName}`);
            continue;
          }
        }
        
        console.log(`   [${i + 1}/${products.length}] Ekleniyor: ${productName} x ${product.quantity}`);
        console.log(`   Fiyat (KDV Dahil): ${(unitPriceInKurus/100).toFixed(2)} TL (${unitPriceInKurus} Kuruş)`);
        
        const addItemResponse = await callFiscalPrinterAPI('inpos-add-item', {
          name: productName,
          unitPrice: unitPriceInKurus,
          multiplier: Math.round(product.quantity * 1000),
          discountRate: 0,
          discountAmount: 0,
          section: product.departmentNo,
          unit: 0,
          timeout: 5000
        }, showAlertModal);
        
        if (!addItemResponse.success) {
          throw new Error(`Ürün eklenemedi: ${productName}`);
        }
        
        console.log(`   ✅ Ürün ${i + 1}/${products.length} eklendi: ${productName}`);
      }
      
      console.log('[Inpos M530] ✅ Belge hazırlandı, ödemeler eklenebilir');
      
      return {
        success: true,
        documentId: 'inpos-live-document', // Geçici ID
      };
      
    } catch (error: any) {
      console.error('[Inpos M530] Belge hazırlama hatası:', error);
      
      // Hata durumunda satışı iptal et
      try {
        console.log('[Inpos M530] ❌ Hata nedeniyle satış iptal ediliyor...');
        await callFiscalPrinterAPI('inpos-cancel-sale', {}, showAlertModal);
        console.log('[Inpos M530] ✅ Satış iptal edildi');
      } catch (cancelError) {
        console.error('[Inpos M530] Satış iptal edilirken hata:', cancelError);
      }
      
      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * "Canlı Belge" Modu - Tek bir ödeme ekle
   */
  async addSinglePayment(
    paymentType: 'pos' | 'cash',
    amount: number,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log(`[Inpos M530] 💳 Tek ödeme ekleniyor: ${paymentType === 'cash' ? 'NAKİT' : 'POS'} - ${amount} TL`);
      
      // ✅ Inpos SDK: AddPayment() kullanılıyor
      // - paymentType: 0 = Kredi Kartı, 1 = Nakit
      // - amount: Kuruş cinsinden
      
      const inposPaymentType = paymentType === 'cash' ? 1 : 0; // ✅ 1 = Nakit, 0 = Kredi Kartı
      const amountInKurus = Math.round(amount * 100); // TL → Kuruş
      
      console.log(`[Inpos M530] API çağrısı: inpos-add-payment (Type=${inposPaymentType}, Amount=${amountInKurus} kuruş)`);
      
      const addPaymentResponse = await callFiscalPrinterAPI('inpos-add-payment', {
        paymentType: inposPaymentType,
        amount: amountInKurus,
        acquirer: 0, // Varsayılan banka
        timeout: paymentType === 'pos' ? 60000 : 5000 // POS için 60 saniye, nakit için 5 saniye
      }, showAlertModal);
      
      if (!addPaymentResponse.success) {
        const apiErrorMsg = addPaymentResponse.data?.message || 'Bilinmeyen hata';
        const isCardRejected = apiErrorMsg.includes('red edildi') || apiErrorMsg.includes('iptal edildi');
        
        if (isCardRejected) {
          console.warn(`[Inpos M530] ❌ Kart red/iptal edildi: ${apiErrorMsg}`);
          throw new Error(apiErrorMsg);
        }
        
        console.warn(`[Inpos M530] ⚠️ ${paymentType} ödeme API yanıtı başarısız! Gerçek durum kontrol ediliyor...`);
        
        const verifyState = await callFiscalPrinterAPI('inpos-get-state', {});
        
        if (verifyState.success) {
          const verifyEcrState = verifyState.data?.data?.ecrStateCode;
          const verifySaleState = verifyState.data?.data?.saleStateCode;
          
          console.log(`[Inpos M530] 🔍 Doğrulama - ECR: ${verifyEcrState}, Sale: ${verifySaleState}`);
          
          // Sadece fiş yazdırma veya satış tamamlanma durumlarında başarılı say
          // WaitingForInput (1) dahil DEĞİL - red sonrası da bu durumda kalıyor
          if (verifyEcrState === 10 || verifyEcrState === 11 || 
              verifySaleState === 0 || verifySaleState === 15) {
            console.log('[Inpos M530] ✅ ÖDEME ASLINDA BAŞARILI! Cihaz fiş yazdırdı/tamamlandı.');
          } else {
            throw new Error(`Ödeme eklenemedi: ${apiErrorMsg}`);
          }
        } else {
          throw new Error(`Ödeme eklenemedi: ${apiErrorMsg}`);
        }
      }
      
      console.log(`[Inpos M530] ✅ ${paymentType === 'cash' ? 'Nakit' : 'POS'} ödeme başarıyla eklendi`);
      
      // ✅ ÇOKLU POS İÇİN ÖNEMLİ: Ödeme eklendikten sonra cihazın WaitingForInput (1) durumuna dönmesini bekle
      // Aksi halde ikinci POS ödemesi eklenemez!
      if (paymentType === 'pos') {
        console.log('[Inpos M530] ⏳ POS ödemesi sonrası WaitingForInput durumuna dönülmesi bekleniyor...');
        
        // 3 saniye kadar bekle (genellikle 1-2 saniye içinde döner)
        for (let i = 0; i < 30; i++) {
          await new Promise(resolve => setTimeout(resolve, 100)); // 100ms bekle
          
          // Durum sorgula
          const stateResponse = await callFiscalPrinterAPI('inpos-get-state', {});
          if (stateResponse.success) {
            const saleState = stateResponse.data?.data?.saleStateCode;
            
            if (saleState === 1) { // WaitingForInput
              console.log('[Inpos M530] ✅ Cihaz WaitingForInput durumuna döndü, yeni ödeme eklenebilir');
              break;
            }
            
            // Eğer DataFinalized (15) durumundaysa, bir sonraki ödeme için hazır
            if (saleState === 15) {
              console.log('[Inpos M530] ⚠️ Cihaz DataFinalized durumunda, bu son ödeme olabilir');
              break;
            }
          }
          
          // Her 10 denemede bir log
          if (i % 10 === 9) {
            console.log(`[Inpos M530] ⏳ Bekleniyor... (${(i + 1) * 100}ms)`);
          }
        }
      }
      
      // ✅ Fiscal data'yı döndür (POS için AuthCode, BatchNo, vb.)
      const fiscalData = addPaymentResponse.data?.data || {};
      
      return {
        success: true,
        fiscalData: {
          ...fiscalData,
          paymentType: paymentType,
          amount: amount,
        },
      };
      
    } catch (error: any) {
      console.error('[Inpos M530] Ödeme ekleme hatası:', error);
      
      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * "Canlı Belge" Modu - Belgeyi kapat
   */
  async closeDocument(
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Inpos M530] 🔒 Belge kapatılıyor...');
      
      // ✅ ÖNCE: Cihaz durumunu kontrol et
      const stateResponse = await callFiscalPrinterAPI('inpos-get-state', {});
      
      if (stateResponse.success) {
        const ecrStateCode = stateResponse.data?.data?.ecrStateCode;
        const saleStateCode = stateResponse.data?.data?.saleStateCode;
        
        console.log(`[Inpos M530] Mevcut durum: ECR=${ecrStateCode}, Sale=${saleStateCode}`);
        
        // ✅ Inpos M530 Özel Davranış: Sepet tutarının tamamı alındığında OTOMATIK KAPANIYOR!
        // Eğer cihaz zaten ana menüdeyse veya Idle ise → Belge zaten kapatılmış!
        if (ecrStateCode === 7 || saleStateCode === 0) {
          console.log('[Inpos M530] ✅ Belge zaten otomatik kapatılmış (Inpos özelliği - tam ödeme)');
          return {
            success: true,
            documentId: 'AUTO-CLOSED',
            fiscalData: {},
          };
        }
        
        // ✅ Eğer DataFinalized (15) ise → Fiş yazdırılıyor, bekle
        if (saleStateCode === 15) {
          console.log('[Inpos M530] ⏳ Belge DataFinalized durumunda, fiş yazdırılıyor...');
          // 2 saniye bekle (fiş yazdırma süresi)
          await new Promise(resolve => setTimeout(resolve, 2000));
          console.log('[Inpos M530] ✅ Belge otomatik kapatıldı (DataFinalized)');
          return {
            success: true,
            documentId: 'AUTO-FINALIZED',
            fiscalData: {},
          };
        }
      }
      
      // ⚠️ Eğer hala Sale modundaysa, manuel kapatma dene
      console.log('[Inpos M530] ⚠️ Belge hala açık, manuel kapatma deneniyor...');
      const finalizeResponse = await callFiscalPrinterAPI('inpos-finalize-sale', { timeout: 10000 }, showAlertModal);
      
      if (!finalizeResponse.success) {
        throw new Error(`Belge kapatılamadı: ${finalizeResponse.data?.message || 'Bilinmeyen hata'}`);
      }
      
      // Fiş bilgilerini al
      const receiptNo = finalizeResponse.data?.data?.receiptNo || 'Bilinmiyor';
      const zNo = finalizeResponse.data?.data?.zNo || 'Bilinmiyor';
      
      console.log(`[Inpos M530] ✅ Belge başarıyla kapatıldı - Fiş No: ${receiptNo}, Z No: ${zNo}`);
      
      return {
        success: true,
        documentId: `${receiptNo}-${zNo}`,
        fiscalData: {
          receiptNo,
          zNo,
          ...finalizeResponse.data?.data,
        },
      };
      
    } catch (error: any) {
      console.error('[Inpos M530] Belge kapatma hatası:', error);
      
      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * "Canlı Belge" Modu - Açık belgeyi iptal et
   */
  async cancelDocument(
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Inpos M530] ❌ Belge iptal ediliyor...');
      
      // ✅ Inpos SDK: CancelSale() kullanılıyor
      const cancelResponse = await callFiscalPrinterAPI('inpos-cancel-sale', {
        timeout: 5000
      }, showAlertModal);
      
      if (!cancelResponse.success) {
        throw new Error(`Belge iptal edilemedi: ${cancelResponse.data?.message || 'Bilinmeyen hata'}`);
      }
      
      console.log('[Inpos M530] ✅ Belge başarıyla iptal edildi');
      
      return {
        success: true,
      };
      
    } catch (error: any) {
      console.error('[Inpos M530] Belge iptal hatası:', error);
      
      return {
        success: false,
        errorMessage: error.message || 'Bilinmeyen hata',
      };
    }
  }

  /**
   * POS ödemesini iade et
   * ⚠️ HENÜZ GELİŞTİRİLMEDİ
   */
  /**
   * POS ödemesini iade et (Belge açıkken)
   * ⚠️ Inpos M530: Belge açıkken iade işlemi DESTEKLENMIYOR!
   */
  async refundPosPayment(
    paymentSequenceNo: number,
    showAlertModal?: (title: string, message: string) => void,
    fiscalData?: any
  ): Promise<FiscalOperationResult> {
    console.warn('[Inpos M530] ⚠️ POS iadesi (belge açıkken) desteklenmiyor!');
    
    showAlertModal?.(
      '⚠️ İade Desteklenmiyor',
      'Inpos M530 yazarkasası belge açıkken iade işlemini desteklememektedir.\n\n' +
      'Belgeyi iptal etmek için "❌ Belgeyi İptal Et" butonunu kullanabilirsiniz.'
    );
    
    return {
      success: false,
      errorMessage: 'Inpos M530 belge açıkken iade işlemini desteklemiyor',
    };
  }

  /**
   * Belge kapandıktan sonra POS ödemesini iade et
   * ⚠️ HENÜZ GELİŞTİRİLMEDİ
   */
  async refundPosPaymentAfterClose(
    amount: number,
    acquirerId: string | undefined,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    console.warn('[Inpos M530] POS iadesi (belge kapandıktan sonra) henüz geliştirilmemiş!');
    showAlertModal?.('Entegrasyon Mevcut Değil', 'Inpos M530 için bu özellik henüz geliştirilmemiştir.');
    return { success: false, errorMessage: 'Inpos M530 entegrasyonu henüz geliştirilmemiş' };
  }

  /**
   * Nakit ödemesini iade et (Belge açıkken)
   * ⚠️ Inpos M530: Belge açıkken iade işlemi DESTEKLENMIYOR!
   */
  async refundCashPayment(
    paymentSequenceNo: number,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    console.warn('[Inpos M530] ⚠️ Nakit iadesi (belge açıkken) desteklenmiyor!');
    
    showAlertModal?.(
      '⚠️ İade Desteklenmiyor',
      'Inpos M530 yazarkasası belge açıkken iade işlemini desteklememektedir.\n\n' +
      'Belgeyi iptal etmek için "❌ Belgeyi İptal Et" butonunu kullanabilirsiniz.'
    );
    
    return {
      success: false,
      errorMessage: 'Inpos M530 belge açıkken iade işlemini desteklemiyor',
    };
  }
}

// Singleton instance
export const inposM530Device = new InposM530Device();

